import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowRight, Clock3, IdCard, ShieldCheck, X } from 'lucide-react';
import './AadharReminder.css';
import { AADHAAR_PLACEHOLDER, aadhaarDigits, aadhaarError, formatAadhaar } from '../../utils/aadhaar';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const REMIND_EVERY_MS = 5 * 60 * 1000; // 5 minutes

// Mounted once at the app root (see App.js) so it persists across every
// page a logged-in member visits — not tied to Dashboard specifically.
// Checks the member's own aadharStatus on login and repeats the check
// (and the popup, if still not submitted) every 5 minutes.
const AadharReminder = () => {
    const { user } = useAuth();
    const [aadharStatus, setAadharStatus] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [aadharInput, setAadharInput] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const intervalRef = useRef(null);

    const checkStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const res = await axios.get(`${API}/api/profiles/my`, { headers: { Authorization: `Bearer ${token}` } });
            const status = res.data.profile?.aadharStatus || 'not_submitted';
            setAadharStatus(status);
            if (status === 'not_submitted') setShowPopup(true);
        } catch (err) {
            // Profile might not exist yet (mid-registration) — nothing to
            // remind about until there's a profile at all.
        }
    };

    useEffect(() => {
        if (!user || user.role !== 'member') {
            if (intervalRef.current) clearInterval(intervalRef.current);
            return;
        }
        checkStatus();
        intervalRef.current = setInterval(checkStatus, REMIND_EVERY_MS);
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const digitsOnly = aadhaarDigits(aadharInput);
        if (!/^\d{12}$/.test(digitsOnly)) {
            toast.error('Aadhar number must be exactly 12 digits');
            return;
        }
        if (aadhaarError(digitsOnly)) {
            toast.error(aadhaarError(digitsOnly));
            return;
        }
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/api/profiles/aadhar`, { aadharNumber: digitsOnly }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Aadhar submitted! Verification takes 24–48 hours.');
            setAadharStatus('pending');
            setShowPopup(false);
            setAadharInput('');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to submit Aadhar');
        } finally {
            setSubmitting(false);
        }
    };

    if (!showPopup || aadharStatus !== 'not_submitted') return null;

    return (
        <div className="aadhar-reminder__overlay">
            <div
                className="aadhar-reminder__modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="aadhar-reminder-title"
                aria-describedby="aadhar-reminder-description"
            >
                <button
                    type="button"
                    className="aadhar-reminder__close"
                    onClick={() => setShowPopup(false)}
                    aria-label="Remind me later and close"
                >
                    <X size={19} strokeWidth={1.8} />
                </button>

                <div className="aadhar-reminder__icon" aria-hidden="true">
                    <IdCard size={31} strokeWidth={1.7} />
                </div>
                <p className="aadhar-reminder__eyebrow">Profile verification</p>
                <h2 className="aadhar-reminder__title" id="aadhar-reminder-title">
                    Complete your Aadhaar verification
                </h2>
                <p className="aadhar-reminder__description" id="aadhar-reminder-description">
                    Verify your identity to build trust with members and unlock full access
                    to your matrimony profile.
                </p>

                <form className="aadhar-reminder__form" onSubmit={handleSubmit}>
                    <label className="aadhar-reminder__label" htmlFor="aadhar-number">
                        Aadhaar number
                        <span>{aadharInput.length}/12</span>
                    </label>
                    <div className="aadhar-reminder__input-wrap">
                        <IdCard size={20} strokeWidth={1.7} aria-hidden="true" />
                        <input
                            id="aadhar-number"
                            className="aadhar-reminder__input"
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            aria-label="12-digit Aadhaar number"
                            placeholder={AADHAAR_PLACEHOLDER}
                            value={formatAadhaar(aadharInput)}
                            maxLength={14}
                            aria-invalid={Boolean(aadhaarError(aadharInput))}
                            onChange={e => setAadharInput(aadhaarDigits(e.target.value))}
                        />
                    </div>
                    {aadhaarError(aadharInput) && <p role="alert" style={{ margin: '6px 4px 0', color: '#c62828', fontSize: '12px' }}>{aadhaarError(aadharInput)}</p>}

                    <div className="aadhar-reminder__privacy-note">
                        <ShieldCheck size={18} strokeWidth={1.8} aria-hidden="true" />
                        <span>Your details are used only for profile verification.</span>
                    </div>

                    <div className="aadhar-reminder__actions">
                        <button
                            type="button"
                            className="aadhar-reminder__button aadhar-reminder__button--secondary"
                            onClick={() => setShowPopup(false)}
                        >
                            <Clock3 size={18} strokeWidth={1.8} aria-hidden="true" />
                            Remind me later
                        </button>
                        <button
                            type="submit"
                            className="aadhar-reminder__button aadhar-reminder__button--primary"
                            disabled={submitting}
                        >
                            {submitting ? 'Submitting...' : 'Verify now'}
                            {!submitting && <ArrowRight size={18} strokeWidth={1.9} aria-hidden="true" />}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AadharReminder;
